/**
 * PortCallStatsCard - Display port call statistics
 *
 * Shows total calls, average/median/min/max duration with
 * clickable min/max to interact with timeline.
 */
import { Card, CardHeader, CardTitle } from '@ais-ui/ui-core';
import { PiClock, PiArrowDown, PiArrowUp, PiChartBar } from 'react-icons/pi';
import { PortCallAggregations, AnalyticsMode } from './types';

interface PortCallStatsCardProps {
  aggregations: PortCallAggregations;
  mode: AnalyticsMode;
  onSelectPortCall?: (portCallId: string) => void;
}

/**
 * Format duration in seconds to human-readable string
 */
const formatDuration = (seconds: number): string => {
  if (seconds === 0) return '-';

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(' ');
};

interface StatItemProps {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  subLabel?: string;
  onClick?: () => void;
  clickable?: boolean;
}

const StatItem = ({
  label,
  value,
  icon,
  subLabel,
  onClick,
  clickable,
}: StatItemProps) => (
  <div
    className={`flex flex-col ${clickable ? 'cursor-pointer hover:bg-muted/50 rounded-md p-2 -m-2 transition-colors' : ''}`}
    onClick={onClick}
  >
    <span className="text-xs text-muted-foreground flex items-center gap-1">
      {icon}
      {label}
    </span>
    <span className="text-lg font-semibold">{value}</span>
    {subLabel && (
      <span
        className="text-xs text-muted-foreground truncate max-w-[120px]"
        title={subLabel}
      >
        {subLabel}
      </span>
    )}
  </div>
);

export const PortCallStatsCard = ({
  aggregations,
  mode,
  onSelectPortCall,
}: PortCallStatsCardProps) => {
  const { totalCalls, completedCalls, duration } = aggregations;

  const handleMinClick = () => {
    if (duration.min && onSelectPortCall) {
      onSelectPortCall(duration.min.portCallId);
    }
  };

  const handleMaxClick = () => {
    if (duration.max && onSelectPortCall) {
      onSelectPortCall(duration.max.portCallId);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <PiChartBar className="h-4 w-4" />
          Port Call Statistics
        </CardTitle>
      </CardHeader>
      <div className="px-6 pb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatItem
            label="Total Calls"
            value={totalCalls}
            icon={<PiChartBar className="h-3 w-3" />}
          />
          <StatItem
            label="Completed"
            value={completedCalls}
            subLabel={
              totalCalls > 0
                ? `${Math.round((completedCalls / totalCalls) * 100)}%`
                : undefined
            }
          />
          <StatItem
            label="Avg Duration"
            value={formatDuration(duration.average)}
            icon={<PiClock className="h-3 w-3" />}
          />
          <StatItem label="Median" value={formatDuration(duration.median)} />
          <StatItem
            label="Shortest"
            value={duration.min ? formatDuration(duration.min.value) : '-'}
            icon={<PiArrowDown className="h-3 w-3" />}
            subLabel={duration.min?.label}
            onClick={handleMinClick}
            clickable={!!duration.min && !!onSelectPortCall}
          />
          <StatItem
            label="Longest"
            value={duration.max ? formatDuration(duration.max.value) : '-'}
            icon={<PiArrowUp className="h-3 w-3" />}
            subLabel={duration.max?.label}
            onClick={handleMaxClick}
            clickable={!!duration.max && !!onSelectPortCall}
          />
        </div>
      </div>
    </Card>
  );
};
